/**
 * Achievements — dedicated page showing all achievement badges.
 *
 * Displays earned and locked achievements for the current user.
 * Accessible via the Achievements button in the top navigation bar.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Award } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { getAllFiles } from '../api/filesApi';
import useAchievements from '../hooks/useAchievements';
import AchievementGrid from '../components/gamification/AchievementGrid';

export default function Achievements() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);

  const loadFiles = useCallback(async () => {
    try {
      const data = await getAllFiles();
      setFiles(data);
    } catch (err) {
      console.error('Failed to load files:', err);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const { earned, allAchievements } = useAchievements(files, user?.email);

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        style={styles.header}
      >
        <Award size={20} color="#0F4C75" />
        <h2 style={styles.title}>Achievements</h2>
        <span style={styles.count}>{earned.length}/{allAchievements.length}</span>
      </motion.div>

      <AchievementGrid earned={earned} allAchievements={allAchievements} />
    </div>
  );
}

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1.25rem',
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
  },
  count: {
    fontSize: '0.8rem',
    fontWeight: 600,
    background: 'rgba(15, 76, 117, 0.08)',
    color: '#0F4C75',
    padding: '0.15rem 0.5rem',
    borderRadius: '999px',
  },
};
